import { useMemo } from "react";

import {
  CATEGORIA_LABEL,
  cartoesDaAlteracao,
  categoriaDaAlteracao,
  formatAlteracoes,
  HISTORICO_DESCARTADA_VAZIA,
  HISTORICO_DETALHE_ERROR_MESSAGE,
  HISTORICO_DETALHE_TITULO,
  NAO_INFORMADO,
  ROTULO_ESTADO_APLICACAO,
  rotuloStatusHistorico,
} from "@/modules/recursos/simulacao";
import { itensDoDetalheHistorico } from "@/modules/recursos/simulacao/simulacao.historico";
import { formatAuditDateTime } from "@/shared/components/audit-date-time/audit-date-time";

import {
  CATEGORIAS,
  ESTADO_TAG,
  ESTADO_TAG_PADRAO,
  SEM_ITENS_TEXTO,
} from "./detalhe-simulacao-dialog.constants";
import { useDetalheSimulacao } from "./hooks/use-detalhe-simulacao";
import type {
  DetalheSimulacaoDialogProps,
  DetalheSimulacaoDialogViewModel,
  GrupoDetalhe,
  MetaDetalhe,
} from "./types-detalhe-simulacao-dialog";

function formatHistoricoData(iso: string | null): string {
  if (!iso) return NAO_INFORMADO;
  const { date, time } = formatAuditDateTime(iso);
  return time ? `${date} ${time}` : date;
}

/** Detalhe de uma simulação do histórico (HU 2221, RF07): resumo e as alterações com o resultado de cada uma. */
export function useDetalheSimulacaoDialog({
  simulacaoId,
  onClose,
}: DetalheSimulacaoDialogProps): DetalheSimulacaoDialogViewModel {
  const { status, detalhe, errorMessage, reload } = useDetalheSimulacao(simulacaoId);

  const itens = useMemo(() => (detalhe ? itensDoDetalheHistorico(detalhe) : []), [detalhe]);

  // Sem relatório (arquivada/descartada, nunca aplicada) o "pendente" é só o padrão da montagem: não vira tag.
  const temRelatorio = Boolean(detalhe?.relatorio);

  const grupos = useMemo<GrupoDetalhe[]>(
    () =>
      CATEGORIAS.flatMap((categoria) => {
        const doGrupo = itens.filter((item) => categoriaDaAlteracao(item.alteracao) === categoria);
        if (doGrupo.length === 0) return [];
        return [
          {
            categoria,
            rotulo: CATEGORIA_LABEL[categoria],
            testId: `detalhe-grupo-${categoria}`,
            alteracoes: doGrupo.map((item) => ({
              chave: item.chave,
              cartoes: cartoesDaAlteracao(item.alteracao).map((cartao, indice) => ({
                cartao,
                falha: indice === 0 && item.estado === "nao-aplicada",
                mensagem: indice === 0 ? item.mensagem : null,
                estado:
                  indice === 0 && temRelatorio
                    ? {
                        estado: item.estado,
                        rotulo: ROTULO_ESTADO_APLICACAO[item.estado] ?? item.estado,
                        ...(ESTADO_TAG[item.estado] ?? ESTADO_TAG_PADRAO),
                      }
                    : null,
              })),
            })),
          },
        ];
      }),
    [itens, temRelatorio],
  );

  const meta: MetaDetalhe[] = detalhe
    ? [
        {
          chave: "status",
          rotulo: "Status",
          valor: rotuloStatusHistorico(detalhe.status),
          testId: "detalhe-status",
        },
        {
          chave: "alteracoes",
          rotulo: "Alterações",
          valor: formatAlteracoes(detalhe.qtdAlteracoes),
          testId: "detalhe-alteracoes",
        },
        ...(detalhe.relatorioTotais
          ? [
              { chave: "aplicadas", rotulo: "Aplicadas", valor: detalhe.relatorioTotais.aplicadas },
              { chave: "com-aviso", rotulo: "Com aviso", valor: detalhe.relatorioTotais.comAviso },
              {
                chave: "nao-aplicadas",
                rotulo: "Não aplicadas",
                valor: detalhe.relatorioTotais.naoAplicadas,
              },
            ]
          : []),
      ]
    : [];

  const descartada = detalhe?.status === "DESCARTADA" && detalhe.qtdAlteracoes === 0;

  return {
    titulo: detalhe
      ? `${HISTORICO_DETALHE_TITULO} — ${rotuloStatusHistorico(detalhe.status)}`
      : HISTORICO_DETALHE_TITULO,
    subtitulo: detalhe
      ? `Criada em ${formatHistoricoData(detalhe.createdAt)}${
          detalhe.aplicadaEm ? ` · Aplicada em ${formatHistoricoData(detalhe.aplicadaEm)}` : ""
        }`
      : null,
    fechar: onClose,
    estado: status,
    mensagem: errorMessage ?? (status === "error" ? HISTORICO_DETALHE_ERROR_MESSAGE : null),
    tentarNovamente: reload,
    meta,
    justificativa: detalhe?.justificativa || null,
    totalAlteracoes: itens.length,
    vazio: descartada
      ? { testId: "detalhe-descartada-vazia", texto: HISTORICO_DESCARTADA_VAZIA }
      : itens.length === 0
        ? { testId: "detalhe-sem-itens", texto: SEM_ITENS_TEXTO }
        : null,
    grupos,
  };
}
